import { Card, CardMedia, Typography, Box, IconButton, Chip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import imagePlaceholder from "../../../../assets/images/sections.jpg";

const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
    }).format(price);
};

// Componente para las etiquetas (Nuevo/Promoción) sobre la imagen
const ProductLabels = ({ product }) => (
    <Box
        sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
        }}
    >
        {product.is_promo && (
            <Chip
                label="Promoción"
                size="small"
                sx={{
                    backgroundColor: '#32CD32',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '22px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
            />
        )}
        {product.is_new && (
            <Chip
                label="Nuevo"
                size="small"
                sx={{
                    backgroundColor: '#d4a017',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '22px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
            />
        )}
    </Box>
);

// ----------------------------------------------------
// 1. Card Horizontal (Imagen Izquierda - Info Derecha)
// ----------------------------------------------------
export const ProductCard = ({
    product,
    onSelect,
    sectionStyles = {},
    buttonColorCards,
    backgroundColorCards,
    textColorCards
}) => {
    // Determinar colores (prioridad: props específicas > sectionStyles > valores por defecto)
    const cardBackgroundColor = backgroundColorCards || sectionStyles.product_background_color || '#ffffff';
    const textColor = textColorCards || sectionStyles.product_text_color || '#222';
    const buttonColor = buttonColorCards || sectionStyles.buttons_color || '#ff0000';
    const borderColor = sectionStyles.product_border_color || '#f0f0f0';

    const augmentedProduct = {
        ...product,
        is_new: product.is_new || (product.id % 5 === 0),
        is_promo: product.is_promo || (product.id % 7 === 0)
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                backgroundColor: cardBackgroundColor,
                borderRadius: '16px',
                boxShadow: `0 4px 12px ${borderColor}80, 0 2px 8px rgba(0,0,0,0.05)`,
                mb: 2,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)'
                },
                maxHeight: "135px"
            }}
            onClick={() => onSelect(augmentedProduct)}
        >
            {/* Contenedor de Imagen (Izquierda) */}
            <Box sx={{ position: 'relative', width: '130px', minWidth: '130px' }}>
                <ProductLabels product={augmentedProduct} />
                <CardMedia
                    component="img"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    image={augmentedProduct.image_url || imagePlaceholder}
                    alt={augmentedProduct.name}
                />
            </Box>

            {/* Contenido (Derecha) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 1.5, sm: 2 }, justifyContent: 'space-between' }}>
                <Box>
                    <Typography component="div" variant="subtitle1" sx={{
                        fontWeight: 700,
                        color: textColor,
                        lineHeight: 1.2,
                        mb: 0.5
                    }}>
                        {augmentedProduct.name}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                        {augmentedProduct.discounted_price ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                    variant="caption"
                                    sx={{ textDecoration: 'line-through', color: 'gray', lineHeight: 1 }}
                                >
                                    {formatPrice(augmentedProduct.price)}
                                </Typography>
                                <Typography variant="subtitle1" sx={{
                                    fontWeight: 900,
                                    color: buttonColor
                                }}>
                                    {formatPrice(augmentedProduct.discounted_price)}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="subtitle1" sx={{
                                fontWeight: 800,
                                color: textColor
                            }}>
                                {formatPrice(augmentedProduct.price)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                    {/* Descripción truncada */}
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            mr: 1,
                            color: textColor
                        }}
                    >
                        {augmentedProduct.description}
                    </Typography>

                    {/* Botón Añadir con color personalizado */}
                    <IconButton
                        onClick={(e) => { e.stopPropagation(); onSelect(augmentedProduct); }}
                        sx={{
                            p: 0,
                            color: buttonColor,
                            '&:hover': {
                                color: buttonColor,
                                backgroundColor: 'transparent',
                                opacity: 0.8
                            }
                        }}
                    >
                        <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                </Box>
            </Box>
        </Card>
    );
};

// ----------------------------------------------------
// 2. Card sin Imagen (Solo Texto)
// ----------------------------------------------------
export const OrderProductCard = ({
    product,
    onSelect,
    sectionStyles = {},
    buttonColorCards,
    backgroundColorCards,
    textColorCards
}) => {
    const cardBackgroundColor = backgroundColorCards || sectionStyles.product_background_color || '#ffffff';
    const textColor = textColorCards || sectionStyles.product_text_color || '#222';
    const buttonColor = buttonColorCards || sectionStyles.buttons_color || '#ff0000';
    const borderColor = sectionStyles.product_border_color || '#e0e0e0';

    const augmentedProduct = {
        ...product,
        is_new: product.is_new || (product.id % 5 === 0),
        is_promo: product.is_promo || (product.id % 7 === 0)
    };

    return (
        <Card
            sx={{
                width: '100%',
                backgroundColor: cardBackgroundColor,
                borderRadius: '16px',
                border: `1px solid ${borderColor}60`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                mb: 2,
                p: 2,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    transform: 'translateY(-3px)'
                },
                height: "135px"
            }}
            onClick={() => onSelect(augmentedProduct)}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, height: "100%" }}>

                {/* 1. Bloque de Texto (Centrado) */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 0 // Crucial para que el truncado de texto funcione en Flexbox
                }}>
                    {/* Etiquetas inline (centradas) */}
                    {(augmentedProduct.is_promo || augmentedProduct.is_new) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                            {augmentedProduct.is_promo && <Chip label="PROMO" size="small" sx={{
                                bgcolor: '#32CD32', color: 'white', fontSize: '0.65rem', height: '22px', fontWeight: 'bold'
                            }} />}
                            {augmentedProduct.is_new && <Chip label="NUEVO" size="small" sx={{
                                bgcolor: '#d4a017', color: 'white', fontSize: '0.65rem', height: '22px', fontWeight: 'bold'
                            }} />}
                        </Box>
                    )}

                    {/* Título */}
                    <Typography variant="h6" sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: textColor,
                        lineHeight: 1.2,
                        mb: 0.5,
                        width: '100%',
                    }}>
                        {augmentedProduct.name}
                    </Typography>

                    {/* Descripción truncada - MODIFICADO: ahora máximo 3 líneas */}
                    {augmentedProduct.description && (
                        <Typography variant="body2" sx={{
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            color: textColor,
                            opacity: 0.7,
                            mb: 0,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,   // Cambiado de 2 a 3
                            width: '100%',
                        }}>
                            {augmentedProduct.description}
                        </Typography>
                    )}
                </Box>

                {/* 2. Bloque de Precio y Botón (Alineados a la derecha) */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexShrink: 0
                }}>
                    {/* Precios */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        {augmentedProduct.discounted_price ? (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: buttonColor, lineHeight: 1 }}>
                                    {formatPrice(augmentedProduct.discounted_price)}
                                </Typography>
                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'gray', opacity: 0.8, lineHeight: 1 }}>
                                    {formatPrice(augmentedProduct.price)}
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textColor, lineHeight: 1 }}>
                                {formatPrice(augmentedProduct.price)}
                            </Typography>
                        )}
                    </Box>

                    {/* Botón de Acción */}
                    <IconButton
                        onClick={(e) => { e.stopPropagation(); onSelect(augmentedProduct); }}
                        sx={{
                            backgroundColor: `${buttonColor}15`,
                            color: buttonColor,
                            p: 1.2,
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: buttonColor,
                                color: cardBackgroundColor,
                                transform: 'scale(1.1)'
                            }
                        }}
                    >
                        <AddCircleOutlineIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                </Box>

            </Box>
        </Card>
    );
};

// ----------------------------------------------------
// 3. Card Horizontal SOLO DISPLAY (sin botón)
// ----------------------------------------------------
export const ProductCardDisplay = ({
    product,
    sectionStyles = {},
    buttonColorCards,
    backgroundColorCards,
    textColorCards,
}) => {
    // Determinar colores (prioridad: props específicas > sectionStyles > valores por defecto)
    const cardBackgroundColor =
        backgroundColorCards ||
        sectionStyles.product_background_color ||
        "#ffffff";
    const textColor = textColorCards || sectionStyles.product_text_color || "#222";
    const buttonColor = buttonColorCards || sectionStyles.buttons_color || "#ff0000";

    const augmentedProduct = {
        ...product,
        is_new: product.is_new || (product.id % 5 === 0),
        is_promo: product.is_promo || (product.id % 7 === 0),
    };

    return (
        <Card
            sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                backgroundColor: cardBackgroundColor,
                borderRadius: "20px",
                boxShadow: `0 8px 20px rgba(0,0,0,0.12), 0 4px 12px ${buttonColor}80`,
                mb: 2,
                overflow: "hidden",
                position: "relative",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                },
            }}
        >
            {/* Contenedor de Imagen (Izquierda) */}
            <Box sx={{ position: "relative", width: "130px", minWidth: "130px", height: "auto" }}>
                <ProductLabels product={augmentedProduct} />
                <CardMedia
                    component="img"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    image={augmentedProduct.image_url || imagePlaceholder}
                    alt={augmentedProduct.name}
                />
            </Box>

            {/* Contenido (Derecha) */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2 },
                    justifyContent: "space-between",
                }}
            >
                <Box>
                    <Typography
                        component="div"
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            color: textColor,
                            lineHeight: 1.2,
                            mb: 0.5,
                        }}
                    >
                        {augmentedProduct.name}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                        {augmentedProduct.discounted_price ? (
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography
                                    variant="caption"
                                    sx={{ textDecoration: "line-through", color: "gray", lineHeight: 1 }}
                                >
                                    {formatPrice(augmentedProduct.price)}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 900,
                                        color: buttonColor,
                                    }}
                                >
                                    {formatPrice(augmentedProduct.discounted_price)}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 800,
                                    color: textColor,
                                }}
                            >
                                {formatPrice(augmentedProduct.price)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Descripción truncada */}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        overflow: "hidden",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        mt: 1,
                        color: textColor,
                    }}
                >
                    {augmentedProduct.description}
                </Typography>
            </Box>
        </Card>
    );
};

// ----------------------------------------------------
// 4. Card sin Imagen SOLO DISPLAY (sin botón)
// ----------------------------------------------------
export const OrderProductCardDisplay = ({
    product,
    sectionStyles = {},
    buttonColorCards,
    backgroundColorCards,
    textColorCards,
}) => {
    const cardBackgroundColor =
        backgroundColorCards ||
        sectionStyles.product_background_color ||
        "#ffffff";
    const textColor = textColorCards || sectionStyles.product_text_color || "#222";
    const buttonColor = buttonColorCards || sectionStyles.buttons_color || "#ff0000";

    const augmentedProduct = {
        ...product,
        is_new: product.is_new || (product.id % 5 === 0),
        is_promo: product.is_promo || (product.id % 7 === 0),
    };

    return (
        <Card
            sx={{
                width: "100%",
                backgroundColor: cardBackgroundColor,
                borderRadius: "20px",
                boxShadow: `0 8px 20px rgba(0,0,0,0.12), 0 4px 12px ${buttonColor}80`,
                mb: 2,
                p: 2,
                position: "relative",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                },
            }}
        >
            <Box sx={{ pr: 2, flex: 1 }}>
                {/* Etiquetas inline para card sin imagen */}
                <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                    {augmentedProduct.is_promo && (
                        <Chip
                            label="PROMO"
                            size="small"
                            sx={{
                                bgcolor: "#32CD32",
                                color: "white",
                                fontSize: "0.6rem",
                                height: "20px",
                            }}
                        />
                    )}
                    {augmentedProduct.is_new && (
                        <Chip
                            label="NUEVO"
                            size="small"
                            sx={{
                                bgcolor: "#d4a017",
                                color: "white",
                                fontSize: "0.6rem",
                                height: "20px",
                            }}
                        />
                    )}
                </Box>

                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        color: textColor,
                        lineHeight: 1.2,
                    }}
                >
                    {augmentedProduct.name}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                    {augmentedProduct.discounted_price ? (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                                variant="caption"
                                sx={{ textDecoration: "line-through", color: "gray", lineHeight: 1 }}
                            >
                                {formatPrice(augmentedProduct.price)}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 900,
                                    color: buttonColor,
                                }}
                            >
                                {formatPrice(augmentedProduct.discounted_price)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 800,
                                color: textColor,
                            }}
                        >
                            {formatPrice(augmentedProduct.price)}
                        </Typography>
                    )}
                </Box>
                {augmentedProduct.description && (
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1,
                            fontSize: "0.8rem",
                            lineHeight: 1.3,
                            color: textColor,
                        }}
                    >
                        {augmentedProduct.description}
                    </Typography>
                )}
            </Box>
        </Card>
    );
};